import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, CheckCircle } from 'lucide-react';
import StripePayment from '../components/StripePayment';
import { Elements } from '@stripe/react-stripe-js';
import { getApiBaseUrl } from '../config/api';
import stripePromise, { STRIPE_APPEARANCE } from '../config/stripe';
import { pagePadding } from '../styles/layout';
import {
  GOLD,
  GOLD_RGBA,
  BG,
  CARD_BG,
  calculateBookingPricing,
  getBookingDurationHours,
  formatAddOnPrice,
} from '../constants/booking';
import { formatDateForApi, formatCalendarDate } from '../utils/bookingDates';

const TEXT_MUTED = '#F5F0E8';
const panelStyle = {
  backgroundColor: CARD_BG,
  borderRadius: '1rem',
  padding: '2rem',
  border: `1px solid ${GOLD_RGBA}, 0.25)`,
};

const Payment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [loadingClientSecret, setLoadingClientSecret] = useState(false);
  const paymentIntentCreatedRef = useRef(false);
  const paymentIntentRequestInFlightRef = useRef(false);
  const paymentCompletedRef = useRef(false);
  
  // Get booking and customer data from location state
  const bookingData = location.state?.bookingData;
  const customerInfo = location.state?.customerInfo;

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Redirect if no data
  useEffect(() => {
    if (!bookingData || !customerInfo) {
      navigate('/booking');
    }
  }, [bookingData, customerInfo, navigate]);

  // Create payment intent on mount (guest checkout supported)
  useEffect(() => {
    const initializePayment = async () => {
      if (!bookingData || !customerInfo) return;
      
      // Don't create a new payment intent if one already exists, is being created, or if payment completed
      if (paymentIntentCreatedRef.current || paymentIntentRequestInFlightRef.current || clientSecret || paymentCompletedRef.current) {
        console.log('✅ Payment intent already exists, request in flight, or payment completed - skipping');
        return;
      }
      
      // Mark that we're creating a payment intent to prevent duplicates
      paymentIntentCreatedRef.current = true;
      paymentIntentRequestInFlightRef.current = true;

      try {
        setLoadingClientSecret(true);
        
        // First, verify API is reachable with a health check
        const baseUrl = getApiBaseUrl();
        if (baseUrl) {
          try {
            const healthUrl = `${baseUrl.replace(/\/$/, '')}/health`;
            const healthResponse = await fetch(healthUrl, { method: 'GET' });
            if (healthResponse.ok) {
              const healthData = await healthResponse.json();
              console.log('✅ API health check passed:', healthData);
            } else {
              console.warn('⚠️ API health check failed:', healthResponse.status);
            }
          } catch (healthError) {
            console.warn('⚠️ API health check error (non-critical):', healthError.message);
          }
        }

        const selectedDateStr = formatDateForApi(bookingData.selectedDate);

        // Build exact URL - getApiBaseUrl() returns value from .env.local in dev
        if (!baseUrl) {
          throw new Error('API base URL is not configured. Please check your environment variables.');
        }
        
        // Ensure baseUrl doesn't end with / and path doesn't start with /
        const cleanBaseUrl = baseUrl.replace(/\/$/, '');
        const url = `${cleanBaseUrl}/payments/create-payment-intent`;
        
        console.log('🔗 Creating PaymentIntent:', { 
          url, 
          baseUrl,
          env: process.env.NODE_ENV,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID
        });
        
        // Validate URL format
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          throw new Error(`Invalid API URL format: ${url}. URL must start with http:// or https://`);
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: null,
            selectedDate: selectedDateStr,
            selectedTime: bookingData.selectedTime,
            packageId: bookingData.selectedPackage.id,
            extraHours: bookingData.extraHours || 0,
            selectedAddOns: (bookingData.selectedAddOns || []).map(addon => addon.id),
            customerInfo: {
              ...customerInfo,
              billingAddress: customerInfo.venueLocation || customerInfo.billingAddress || '',
            },
          }),
        });

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        
        if (!response.ok) {
          let errorMessage = `Payment API error: ${response.status}`;
          let errorDetails = null;
          
          // If response is HTML (404 page, error page, etc.), get text instead
          if (!isJson) {
            try {
              const text = await response.text();
              console.error('❌ PaymentIntent response is HTML (not JSON):', {
                status: response.status,
                statusText: response.statusText,
                url,
                contentType,
                preview: text.substring(0, 200)
              });
              
              // Check if it's a 404
              if (response.status === 404) {
                errorMessage = `API endpoint not found (404). Check that the URL is correct: ${url}`;
              } else if (text.includes('<!doctype') || text.includes('<html')) {
                errorMessage = `Server returned HTML instead of JSON. This usually means the endpoint doesn't exist or the server crashed. URL: ${url}`;
              } else {
                errorMessage = `Payment API error: ${response.status} - ${text.substring(0, 100)}`;
              }
            } catch (textError) {
              console.error('PaymentIntent response error (could not parse):', response.status, textError);
              errorMessage = `Payment API error: ${response.status} - Could not read response`;
            }
          } else {
            // Response is JSON, try to parse it
            try {
              const errorData = await response.json();
              errorMessage = errorData.error || errorMessage;
              errorDetails = errorData;
            } catch (parseError) {
              console.error('PaymentIntent response error (JSON parse failed):', response.status, parseError);
            }
          }
          
          console.error('❌ PaymentIntent creation failed:', {
            status: response.status,
            statusText: response.statusText,
            url,
            contentType,
            error: errorMessage,
            details: errorDetails
          });
          
          throw new Error(errorMessage);
        }

        // Check if response is JSON before parsing
        if (!isJson) {
          const text = await response.text();
          console.error('❌ PaymentIntent response is not JSON:', {
            url,
            contentType,
            preview: text.substring(0, 200)
          });
          throw new Error(`Server returned ${contentType || 'non-JSON'} instead of JSON. URL: ${url}`);
        }

        const data = await response.json();
        const { clientSecret: secret } = data;
        
        if (!secret) {
          console.error('❌ No client secret in response:', data);
          throw new Error('No client secret returned from server');
        }

        console.log('✅ PaymentIntent created successfully');

        // Extract payment intent ID from client secret (format: pi_xxx_secret_xxx)
        const paymentIntentMatch = secret.match(/pi_[^_]+/);
        if (paymentIntentMatch) {
          setPaymentIntentId(paymentIntentMatch[0]);
        }

        setClientSecret(secret);
        paymentIntentRequestInFlightRef.current = false;
      } catch (err) {
        console.error('❌ Error initializing payment:', {
          message: err.message,
          stack: err.stack,
          name: err.name
        });
        
        // Reset refs on error so user can retry
        paymentIntentCreatedRef.current = false;
        paymentIntentRequestInFlightRef.current = false;
        
        // Provide user-friendly error message
        let userMessage = err.message || 'Failed to initialize payment. Please try again.';
        
        // Network errors (CORS, connection refused, etc.)
        if (err.message.includes('Failed to fetch') || err.name === 'TypeError') {
          userMessage = 'Unable to connect to payment server. Please check your internet connection and try again.';
        }
        
        setError(userMessage);
      } finally {
        setLoadingClientSecret(false);
      }
    };
    initializePayment();
  }, [bookingData, customerInfo, clientSecret]);

  const calculateTotal = () => {
    if (!bookingData) return { total: 0, deposit: 0 };
    const durationHours = getBookingDurationHours(bookingData);
    return calculateBookingPricing(
      bookingData.selectedPackage,
      durationHours,
      bookingData.selectedAddOns || []
    );
  };

  const { total, deposit } = calculateTotal();

  const handlePaymentSuccess = async (paymentIntent) => {
    // Prevent duplicate success handling
    if (paymentCompletedRef.current) {
      console.log('⚠️ Payment already completed, ignoring duplicate success call');
      return;
    }
    
    paymentCompletedRef.current = true;
    setIsProcessing(true);
    setError(null);
    
    // Booking will be created by webhook - just redirect to success page
    setMessage('Payment successful! Redirecting...');
    
    const piId = paymentIntent?.id || paymentIntentId;
    if (!piId) {
      setError('Payment succeeded but could not get payment intent ID');
      setIsProcessing(false);
      paymentCompletedRef.current = false; // Reset on error
      return;
    }

    // Redirect immediately - success page will poll for booking
    navigate('/booking-success', {
      state: {
        paymentIntentId: piId,
        bookingData,
        customerInfo
      }
    });
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
  };

  if (!bookingData || !customerInfo) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: BG,
        padding: pagePadding(isMobile),
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#F5F0E8', marginBottom: '1rem' }}>No booking data found</h2>
          <p style={{ color: TEXT_MUTED, marginBottom: '2rem' }}>Please start your booking process again.</p>
          <button
            onClick={() => navigate('/booking')}
            style={{
              backgroundColor: GOLD,
              color: '#000000',
              border: 'none',
              borderRadius: '0.75rem',
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Start Booking
          </button>
        </div>
      </div>
    );
  }

  const durationHours = getBookingDurationHours(bookingData);

  return (
    <div className="dd-page dd-payment" style={{ backgroundColor: BG, padding: pagePadding(isMobile) }}>
      <div style={{ maxWidth: '75rem', margin: '0 auto', width: '100%' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1
            style={{
              fontSize: isMobile ? '1.875rem' : '2.5rem',
              fontWeight: '800',
              color: GOLD,
              marginBottom: '0.5rem',
            }}
          >
            Secure Payment
          </h1>
          <p
            style={{
              fontSize: '1.0625rem',
              color: TEXT_MUTED,
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            Complete your booking with secure payment processing
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/customer-info', { state: { bookingData } })}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backgroundColor: 'transparent',
            border: `1px solid ${GOLD_RGBA}, 0.35)`,
            borderRadius: '0.75rem',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            color: '#F5F0E8',
            cursor: 'pointer',
            marginBottom: '2rem',
          }}
        >
          <ArrowLeft size={16} />
          Back to Details
        </button>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
          gap: '2rem',
          alignItems: 'start'
        }}>
          
          <div style={panelStyle}>
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                color: GOLD,
              }}
            >
              Booking Summary
            </h2>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: '600', color: '#F5F0E8', marginBottom: '0.75rem' }}>
                {bookingData.selectedPackage.name}
              </h3>
              <p style={{ color: TEXT_MUTED, marginBottom: '0.5rem' }}>
                {formatCalendarDate(bookingData.selectedDate, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <p style={{ color: TEXT_MUTED, marginBottom: '0.5rem' }}>
                {bookingData.selectedTime} · {durationHours} hours
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: '600', color: '#F5F0E8', marginBottom: '0.75rem' }}>
                Add-ons
              </h3>
              {bookingData.selectedAddOns?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  {bookingData.selectedAddOns.map((addon) => (
                    <p key={addon.id} style={{ color: TEXT_MUTED, fontSize: '0.875rem' }}>
                      {addon.name} — {formatAddOnPrice(addon)}
                    </p>
                  ))}
                </div>
              ) : (
                <p style={{ color: TEXT_MUTED, fontSize: '0.875rem' }}>No add-ons selected</p>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: '600', color: '#F5F0E8', marginBottom: '0.75rem' }}>
                Customer Information
              </h3>
              <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {customerInfo.fullName}
              </p>
              <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {customerInfo.email}
              </p>
              <p style={{ color: TEXT_MUTED, fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                {customerInfo.phoneNumber}
              </p>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: '600', color: '#F5F0E8', marginBottom: '0.75rem' }}>
                Venue Location
              </h3>
              <p style={{ color: TEXT_MUTED, fontSize: '0.875rem' }}>{customerInfo.venueLocation}</p>
            </div>

            <div
              style={{
                borderTop: `1px solid ${GOLD_RGBA}, 0.2)`,
                paddingTop: '1.5rem',
              }}
            >
              <div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#F5F0E8' }}>Total: ${total}</div>
              <div style={{ fontSize: '0.875rem', color: TEXT_MUTED, marginTop: '0.35rem' }}>
                Deposit due today: <span style={{ color: GOLD, fontWeight: '700' }}>${deposit}</span>
              </div>
            </div>
          </div>

          <div className="dd-stripe-panel" style={panelStyle}>
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                color: '#F5F0E8',
              }}
            >
              <CreditCard size={24} style={{ marginRight: '0.5rem', color: GOLD }} />
              Payment Information
            </h2>

            <div
              style={{
                backgroundColor: BG,
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: '1.5rem',
                border: `1px solid ${GOLD_RGBA}, 0.15)`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: '600', color: '#F5F0E8' }}>Amount Due Today</div>
                  <div style={{ fontSize: '0.875rem', color: TEXT_MUTED }}>
                    25% deposit to secure your booking
                  </div>
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: GOLD }}>${deposit}</div>
              </div>

              <div
                style={{
                  borderTop: `1px solid ${GOLD_RGBA}, 0.15)`,
                  paddingTop: '1rem',
                  marginTop: '1rem',
                  fontSize: '0.875rem',
                  color: TEXT_MUTED,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <span>Remaining balance (${total - deposit})</span>
                <span>Due 3 days before event</span>
              </div>
            </div>

            {loadingClientSecret && (
              <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>
                Initializing payment...
              </div>
            )}

             {error && !loadingClientSecret && (
               <div style={{ 
                 color: '#dc2626', 
                 padding: '1rem', 
                 backgroundColor: '#fee2e2', 
                 borderRadius: '0.5rem', 
                 marginBottom: '1rem',
                 border: '1px solid #fecaca'
               }}>
                 <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>⚠️ Payment Error</div>
                 <div>{error}</div>
                 {error.includes('connection') && (
                   <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#991b1b' }}>
                     💡 Tip: Check your internet connection and try again. If the problem persists, please contact support.
                   </div>
                 )}
               </div>
             )}

             {clientSecret && !error && (
               <Elements
                 key={clientSecret}
                 stripe={stripePromise}
                 options={{
                   clientSecret,
                   appearance: STRIPE_APPEARANCE,
                 }}
               >
                 <StripePayment
                   onSuccess={handlePaymentSuccess}
                   onError={handlePaymentError}
                   isProcessing={isProcessing}
                   customerInfo={customerInfo}
                   clientSecret={clientSecret}
                 />
               </Elements>
             )}

            {/* Error Message */}
            {error && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '2px solid #fecaca',
                borderRadius: '0.75rem',
                padding: '1rem',
                color: '#dc2626',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginTop: '1rem'
              }}>
                {error}
              </div>
            )}

            {/* Success Message */}
            {message && (
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '2px solid #bbf7d0',
                borderRadius: '0.75rem',
                padding: '1rem',
                color: '#16a34a',
                fontSize: '0.875rem',
                fontWeight: '500',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <CheckCircle size={16} />
                {message}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
